import { createI18n, type CreateI18nOptions } from '@saniplus/api-client';
import type { Locale } from '@saniplus/contracts';
import type { i18n as I18nInstance } from 'i18next';

import frAudit from './fr/audit.json';
import frAuth from './fr/auth.json';
import frCommon from './fr/common.json';
import frDashboard from './fr/dashboard.json';
import frExceptions from './fr/exceptions.json';
import frHeures from './fr/heures.json';
import frHoraires from './fr/horaires.json';
import frNav from './fr/nav.json';
import frOrganisation from './fr/organisation.json';
import frPersonnel from './fr/personnel.json';
import frPointages from './fr/pointages.json';
import frProfil from './fr/profil.json';
import frSettings from './fr/settings.json';
import frTaches from './fr/taches.json';

/**
 * D-13: catalogues are split per module (`src/i18n/{fr,en}/<module>.json`), and
 * every one of them is registered here. A feature that ships a catalogue and is
 * not in this map renders raw keys — the failure is silent in the browser and
 * loud only in a screenshot, so the list is kept exhaustive on purpose.
 *
 * Only **French** is bundled. French is the product language, `index.html`
 * hardcodes `lang="fr-CA"`, and the English half is the same size again — so it
 * lives in `./en-catalogues` and is fetched the first time anybody asks for
 * English (see `initI18n`).
 *
 * `common` carries the login screen's field labels and generic errors
 * (`login.employeeNumber`, `login.pin`, `login.submit`, `login.errors.*`)
 * even though the screen itself lives in `features/auth` — `features/auth`'s
 * own `login.*` keys are the form's helper text and policy copy instead;
 * `LoginPage.tsx` reads from both namespaces.
 */
export const resources: CreateI18nOptions['resources'] = {
  fr: {
    common: frCommon,
    auth: frAuth,
    nav: frNav,
    dashboard: frDashboard,
    horaires: frHoraires,
    pointages: frPointages,
    taches: frTaches,
    heures: frHeures,
    exceptions: frExceptions,
    organisation: frOrganisation,
    personnel: frPersonnel,
    audit: frAudit,
    settings: frSettings,
    profil: frProfil,
  },
};

export const namespaces = [
  'common',
  'auth',
  'nav',
  'dashboard',
  'horaires',
  'pointages',
  'taches',
  'heures',
  'exceptions',
  'organisation',
  'personnel',
  'audit',
  'settings',
  'profil',
];

/**
 * Registers the English catalogues on an instance, fetching the chunk once.
 *
 * Exported because `initI18n` is synchronous and English is not: anything that
 * asserts an English string without going through `changeLanguage` — a spec
 * calling `getFixedT('en', ...)`, a render helper handed `locale: 'en'` — has
 * to await this first.
 *
 * The chunk itself is cached by the module system; the *registration* is not,
 * because every `initI18n` call is a fresh isolated instance (one per app, one
 * per test) and each needs its own bundles.
 */
let cataloguesPromise: Promise<Record<string, unknown>> | null = null;

export async function ensureEnglish(i18n: I18nInstance): Promise<void> {
  if (i18n.hasResourceBundle('en', 'common')) return;
  cataloguesPromise ??= import('./en-catalogues').then((m) => m.default);
  const catalogues = await cataloguesPromise;
  for (const [ns, bundle] of Object.entries(catalogues)) {
    i18n.addResourceBundle('en', ns, bundle, true, true);
  }
}

export function initI18n(defaultLocale: Locale) {
  const i18n = createI18n({ resources, defaultLocale, namespaces });

  /*
   * `changeLanguage` is wrapped rather than left to a `languageChanged`
   * listener so that the promise every caller already awaits — `useLocale`,
   * the specs — resolves with English *on screen*, not with English still in
   * flight. Nothing switches language until the catalogue is in the instance.
   */
  const changeLanguage = i18n.changeLanguage.bind(i18n);
  i18n.changeLanguage = async (lng, callback) => {
    if (lng !== undefined && lng.startsWith('en')) await ensureEnglish(i18n);
    return changeLanguage(lng, callback);
  };

  // A stored `en` preference: init already resolved to English, so the only
  // thing left is to fill it in and repaint. French shows until it lands.
  if (defaultLocale === 'en') {
    void ensureEnglish(i18n).then(() => i18n.emit('languageChanged', i18n.language));
  }

  return i18n;
}
